// 问答历史记录服务
export interface QARecord {
  id: string;
  question: string;
  answer: string;
  timestamp: Date;
  conversationId?: string;
  similarity?: number; // 相似度得分
}

export interface SimilarQuestion {
  record: QARecord;
  similarity: number;
}

class QAHistoryService {
  private readonly STORAGE_KEY = 'qa_history';
  private readonly SIMILARITY_THRESHOLD = 0.7; // 相似度阈值

  // 获取所有历史记录
  getAllRecords(): QARecord[] {
    const data = localStorage.getItem(this.STORAGE_KEY);
    if (!data) return [];
    
    const records = JSON.parse(data);
    // 转换日期字符串为Date对象
    return records.map((record: any) => ({
      ...record,
      timestamp: new Date(record.timestamp)
    }));
  }

  // 添加新的问答记录
  addRecord(question: string, answer: string, conversationId?: string): QARecord {
    const records = this.getAllRecords();
    const newRecord: QARecord = {
      id: this.generateId(),
      question,
      answer,
      timestamp: new Date(),
      conversationId
    };
    
    records.push(newRecord);
    this.saveRecords(records);
    return newRecord;
  }

  // 删除记录
  deleteRecord(id: string): boolean {
    const records = this.getAllRecords();
    const filteredRecords = records.filter(record => record.id !== id);
    
    if (filteredRecords.length < records.length) {
      this.saveRecords(filteredRecords);
      return true;
    }
    return false;
  }

  // 清空所有记录
  clearAllRecords(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // 搜索历史记录
  searchRecords(keyword: string): QARecord[] {
    const records = this.getAllRecords();
    const lowerKeyword = keyword.toLowerCase();
    
    return records.filter(record => 
      record.question.toLowerCase().includes(lowerKeyword) ||
      record.answer.toLowerCase().includes(lowerKeyword)
    );
  }

  // 计算文本相似度（简单的基于词汇重叠的算法）
  private calculateSimilarity(text1: string, text2: string): number {
    // 转换为小写并分词
    const words1 = this.tokenize(text1.toLowerCase());
    const words2 = this.tokenize(text2.toLowerCase());
    
    // 计算Jaccard相似度
    const intersection = new Set(words1.filter(word => words2.includes(word)));
    const union = new Set([...words1, ...words2]);
    
    if (union.size === 0) return 0;
    return intersection.size / union.size;
  }

  // 中文分词（简单实现，实际应用中可以使用更复杂的分词算法）
  private tokenize(text: string): string[] {
    // 移除标点符号
    const cleanText = text.replace(/[，。！？；：""''（）【】《》、]/g, ' ');
    
    // 按空格分词
    const words = cleanText.split(/\s+/).filter(word => word.length > 0);
    
    // 对于中文，还需要进行单字分词（简单处理）
    const tokens: string[] = [];
    words.forEach(word => {
      if (/[\u4e00-\u9fa5]/.test(word)) {
        // 如果是中文，进行2-gram分词
        for (let i = 0; i < word.length - 1; i++) {
          tokens.push(word.substring(i, i + 2));
        }
        // 同时保留整个词
        if (word.length > 1) {
          tokens.push(word);
        }
      } else {
        tokens.push(word);
      }
    });
    
    return tokens;
  }

  // 查找相似问题
  findSimilarQuestions(question: string, limit: number = 5): SimilarQuestion[] {
    const records = this.getAllRecords();
    const similarities: SimilarQuestion[] = [];
    
    records.forEach(record => {
      const similarity = this.calculateSimilarity(question, record.question);
      if (similarity >= this.SIMILARITY_THRESHOLD) {
        similarities.push({ record, similarity });
      }
    });
    
    // 按相似度降序排序
    similarities.sort((a, b) => b.similarity - a.similarity);
    
    // 返回前N个最相似的问题
    return similarities.slice(0, limit);
  }

  // 检查是否有高度相似的问题
  hasSimilarQuestion(question: string, threshold: number = 0.9): SimilarQuestion | null {
    const similarQuestions = this.findSimilarQuestions(question, 1);
    if (similarQuestions.length > 0 && similarQuestions[0].similarity >= threshold) {
      return similarQuestions[0];
    }
    return null;
  }

  // 获取按时间分组的记录
  getRecordsByDate(): Map<string, QARecord[]> {
    const records = this.getAllRecords();
    const grouped = new Map<string, QARecord[]>();
    
    records.forEach(record => {
      const dateKey = this.formatDate(record.timestamp);
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(record);
    });
    
    return grouped;
  }

  // 保存记录到localStorage
  private saveRecords(records: QARecord[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(records));
  }

  // 生成唯一ID
  private generateId(): string {
    return `qa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // 格式化日期
  private formatDate(date: Date): string {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (this.isSameDay(date, today)) {
      return '今天';
    } else if (this.isSameDay(date, yesterday)) {
      return '昨天';
    } else {
      return date.toLocaleDateString('zh-CN');
    }
  }

  // 判断是否同一天
  private isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }
}

// 导出单例
export const qaHistoryService = new QAHistoryService();